/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  listProjectTags,
  getStandardTags,
  createTag,
  updateTag,
  deleteTag,
  getTagStatistics,
} from '@/lib/api/tags'
import { supabase } from '@/lib/supabase'
import type { TagCreateInput, TagUpdateInput } from '@/types/database'

/**
 * Fetch all tags for a project (standard + custom)
 */
export function useTags(
  projectId: string | undefined,
  includeStandard = true,
  sortBy: 'name' | 'usage_count' | 'created_at' = 'name'
) {
  return useQuery({
    queryKey: ['tags', projectId, includeStandard, sortBy],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID is required')

      // Add timeout to prevent infinite hanging
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 15000) // 15 second timeout

      try {
        const result = await listProjectTags(projectId, includeStandard, sortBy)
        clearTimeout(timeoutId)
        return result
      } catch (error) {
        clearTimeout(timeoutId)
        throw error
      }
    },
    enabled: !!projectId,
  })
}

/**
 * Fetch standard tags only (VFX, Dailies, Stock, Graphics, Audio)
 */
export function useStandardTags() {
  return useQuery({
    queryKey: ['tags', 'standard'],
    queryFn: getStandardTags,
  })
}

/**
 * Create a new custom tag
 */
export function useCreateTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: TagCreateInput }) =>
      createTag(projectId, data),
    onSuccess: (_, { projectId }) => {
      toast.success('Tag created successfully')
      queryClient.invalidateQueries({ queryKey: ['tags', projectId] })
      queryClient.invalidateQueries({ queryKey: ['tagStatistics', projectId] })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create tag')
    },
  })
}

/**
 * Update a tag
 */
export function useUpdateTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ tagId, data }: { tagId: string; data: TagUpdateInput; projectId: string }) =>
      updateTag(tagId, data),
    onSuccess: (_, { projectId }) => {
      toast.success('Tag updated successfully')
      queryClient.invalidateQueries({ queryKey: ['tags', projectId] })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update tag')
    },
  })
}

/**
 * Delete a tag
 */
export function useDeleteTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ tagId }: { tagId: string; projectId: string }) =>
      deleteTag(tagId),
    onSuccess: (_, { projectId }) => {
      toast.success('Tag deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['tags', projectId] })
      queryClient.invalidateQueries({ queryKey: ['tagStatistics', projectId] })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete tag')
    },
  })
}

/**
 * Get tag statistics for a project
 */
export function useTagStatistics(projectId: string | undefined) {
  return useQuery({
    queryKey: ['tagStatistics', projectId],
    queryFn: () => {
      if (!projectId) throw new Error('Project ID is required')
      return getTagStatistics(projectId)
    },
    enabled: !!projectId,
  })
}

/**
 * Update clip tags directly via Supabase
 * (Alternative to backend API for simple updates)
 */
export function useUpdateClipTags() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ clipId, tags }: { clipId: string; tags: string[] }) => {
      const { data, error } = await supabase
        .from('clips')
        .update({ tags })
        .eq('id', clipId)
        .select()
        .maybeSingle()

      if (error) {
        throw error
      }
      if (!data) {
        throw new Error('Clip not found or update not allowed')
      }
      return data
    },
    onSuccess: (clip) => {
      toast.success('Tags updated')
      queryClient.invalidateQueries({ queryKey: ['clips'] })
      queryClient.invalidateQueries({ queryKey: ['clip', clip.id] })
      queryClient.invalidateQueries({ queryKey: ['tagStatistics', clip.project_id] })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update tags')
    },
  })
}
